import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { api } from '../services/api'
import Button from '../components/Button'

export default function FormSubmissions() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [expandedSubmission, setExpandedSubmission] = useState(null)

  const { data: formRes } = useQuery({
    queryKey: ['form', id],
    queryFn: () => api.get(`/forms/${id}`).then((res) => res.data),
    enabled: !!id,
  })

  const { data: submissionsRes, isLoading } = useQuery({
    queryKey: ['form-submissions', id],
    queryFn: () => api.get(`/forms/${id}/submissions`).then((res) => res.data),
    enabled: !!id,
  })

  const deleteSubmissionMutation = useMutation({
    mutationFn: (submissionId) =>
      api.delete(`/forms/${id}/submissions/${submissionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['form-submissions', id])
    },
  })

  const form = formRes?.data
  const submissions = submissionsRes?.data || []

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="secondary" onClick={() => navigate('/forms')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {form?.name} - Submissions
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage form submissions.
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : submissions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No submissions yet.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {submissions.map((submission) => {
              const isExpanded = expandedSubmission === submission.id
              return (
                <div key={submission.id} className="bg-white">
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() =>
                      setExpandedSubmission(isExpanded ? null : submission.id)
                    }
                  >
                    <div className="flex items-center space-x-3">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Submission #{submission.id}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(submission.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-xs text-gray-400">
                        {submission.ip_address}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (
                            confirm(
                              'Are you sure you want to delete this submission?'
                            )
                          ) {
                            deleteSubmissionMutation.mutate(submission.id)
                          }
                        }}
                        className="text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                      <div className="mt-4 space-y-3">
                        {Object.entries(submission.data || {}).map(
                          ([key, value]) => (
                            <div key={key} className="grid grid-cols-3 gap-4">
                              <div className="text-sm font-medium text-gray-700">
                                {key}
                              </div>
                              <div className="col-span-2 text-sm text-gray-900">
                                {formatValue(value)}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                      {submission.user_agent && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-xs text-gray-400">
                            User Agent: {submission.user_agent}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

